import React, { useState } from 'react';

import classNames from 'classnames';
import { Col, Row, Button } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faFilter, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { useDebounce } from 'use-debounce';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  purple1,
  white,
} from 'lib/css/colors';
import ActiveFilterChips from 'components/shared/ActiveFilterChips';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import {
  GLOBAL_VENDORS,
  GLOBAL_VENDORS_DOWNLOAD_CSV,
  GLOBAL_VENDORS_DOWNLOAD_STATS_CSV,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import ManageVendorsTable from 'components/manage_vendors/ManageVendorsTable';
import PaginationComponent from 'components/shared/PaginationComponent';
import SearchBar from 'components/shared/SearchBar';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import VendorCreateModal from 'components/manage_vendors/VendorCreateModal';
import VendorFilters from 'components/manage_vendors/filters/VendorFilters';
import { VendorConfigurationStatusOptions } from 'lib/enums/VendorConfigurationStatus';

function ManageVendorsView() {
  const hasUserPermissions = useUserPermissions();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery] = useDebounce(searchInput, 200);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);
  const [isDPPartner, setIsDPPartner] = useState('');
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);

  const { data: { vendors, count } = {}, loading } = useGet(GLOBAL_VENDORS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    query: searchQuery,
    is_dp_partner: isDPPartner,
  });

  const { postData: exportVendorDetails, loading: vendorsExporting } = usePost(
    GLOBAL_VENDORS_DOWNLOAD_CSV,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while.
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export the file',
      }),
  );

  const { postData: exportStats, loading: statsExporting } = usePost(
    GLOBAL_VENDORS_DOWNLOAD_STATS_CSV,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The DP partner stats CSV will be generated in a while.
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export stats',
      }),
  );

  const activeFilters = [
    isDPPartner && {
      label: 'DP Partner',
      value:
        VendorConfigurationStatusOptions.find(option => option.value === isDPPartner)?.label ||
        isDPPartner,
      onRemove: () => setIsDPPartner(''),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setIsDPPartner('');
  };

  return (
    <div className={css(styles.pageContainer)}>
      {/* Header Section */}
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Manage Distributors</h1>
        </div>
      </div>

      {/* Main Content */}
      <Container className={css(styles.mainContent)}>
        <Row className={css(styles.toolbarRow)}>
          <Col xs={5} className="d-flex align-items-center">
            <div className={css(styles.searchWrapper)}>
              <SearchBar
                value={searchInput}
                setValue={setSearchInput}
                placeholder="Search by distributor name"
              />
            </div>
            <Button
              className={css(styles.filterButton)}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Col>
          <Col xs={7} className={css(styles.actionButtonsCol)}>
            <Button
              className={css(styles.primaryButton)}
              onClick={() => setShowCreateVendorModal(true)}
            >
              + New Distributor
            </Button>
            {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
              <>
                <Button
                  className={css(styles.outlineButton)}
                  onClick={() => exportVendorDetails({ query: searchQuery })}
                  disabled={vendorsExporting}
                >
                  <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                  Export
                </Button>
                <Button
                  className={css(styles.outlineButton)}
                  onClick={() => exportStats({})}
                  disabled={statsExporting}
                >
                  <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                  Export Stats
                </Button>
              </>
            )}
          </Col>
        </Row>
        <Row>
          <Col className="d-flex align-items-center flex-wrap mt-3">
            <span className={classNames(css(styles.selectedText), 'text-secondary ml-2 mr-3')}>
              {count} Distributors found
            </span>
            <ActiveFilterChips filters={activeFilters} onClearAll={clearAllFilters} />
          </Col>
          <Col className={classNames(css(styles.pagination), 'd-flex justify-content-end')}>
            <PaginationComponent
              totalItems={count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              defaultSize
            />
          </Col>
        </Row>
        <div className={css(styles.scrollableContainer)}>
          {loading ? <LoadingSpinner short /> : <ManageVendorsTable vendors={vendors} />}
        </div>
        {showFilters && (
          <div className={css(styles.vendorFiltersOverlay)}>
            <VendorFilters
              isDPPartner={isDPPartner}
              setIsDPPartner={setIsDPPartner}
              setShowFilters={setShowFilters}
              loading={loading}
            />
          </div>
        )}
        {showCreateVendorModal ? (
          <VendorCreateModal onHide={() => setShowCreateVendorModal(false)} />
        ) : null}
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  mainContent: {
    padding: '24px 40px',
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    maxWidth: 300,
  },
  filterButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 500,
    marginLeft: 12,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: `${grape}15`,
      borderColor: grape,
    },
  },
  actionButtonsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
    },
  },
  outlineButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `2px solid ${grape}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
      color: white,
    },
  },
  scrollableContainer: {
    height: '70vh',
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflow: 'auto',
  },
  vendorFiltersOverlay: {
    position: 'absolute',
    padding: '1.5rem',
    top: 40,
    right: 0,
    bottom: 0,
    width: '30vw',
    minHeight: '100vh',
    backgroundColor: white,
    borderLeft: `4px solid ${grape}`,
    zIndex: 999,
    visibility: 'visible',
  },
  pagination: {
    marginTop: '1rem',
    zIndex: 2,
  },
  selectedText: {
    fontSize: '1rem',
    color: coolGrey,
  },
});

export default ManageVendorsView;
